import { Component, OnInit } from '@angular/core'
import { BackendService } from 'src/app/services/backend.service'
import Swal from 'sweetalert2'

@Component({
    selector: 'app-admin-functions',
    templateUrl: './admin-functions.component.html',
    styleUrls: ['./admin-functions.component.scss'],
})
export class AdminFunctionsComponent implements OnInit {
    isSyncing: boolean = false
    isSyncingSubscriptions: boolean = false
    isSendingReviewRequests: boolean = false
    isMigratingReviewRequested: boolean = false
    isResettingIncorrectConverted: boolean = false

    constructor(private backendService: BackendService) {}

    ngOnInit(): void {}

    async syncEmailData() {
        if (this.isSyncing) return

        const confirmResult = await Swal.fire({
            title: 'Sync Email Data?',
            text: 'This will update email addresses and auth data from Firebase Auth. The process may take several minutes. This is a one-time migration function.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, sync now',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.isSyncing = true

        try {
            await this.backendService.syncAdminClientsAuthData()

            await Swal.fire({
                title: 'Success!',
                text: 'Email data has been synced successfully.',
                icon: 'success',
                confirmButtonText: 'OK',
            })
        } catch (error) {
            console.error('Error syncing email data:', error)
            await Swal.fire({
                title: 'Error',
                text: 'Failed to sync email data. Please try again or check the console for details.',
                icon: 'error',
                confirmButtonText: 'OK',
            })
        } finally {
            this.isSyncing = false
        }
    }

    async syncSubscriptionData() {
        if (this.isSyncingSubscriptions) return

        const confirmResult = await Swal.fire({
            title: 'Sync Subscription Data?',
            text: 'This will update subscription information for all clients. The process may take several minutes.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, sync now',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.isSyncingSubscriptions = true

        try {
            await this.backendService.syncAdminClientsSubscriptionData()

            await Swal.fire({
                title: 'Success!',
                text: 'Subscription data has been synced successfully.',
                icon: 'success',
                confirmButtonText: 'OK',
            })
        } catch (error) {
            console.error('Error syncing subscription data:', error)
            await Swal.fire({
                title: 'Error',
                text: 'Failed to sync subscription data. Please try again or check the console for details.',
                icon: 'error',
                confirmButtonText: 'OK',
            })
        } finally {
            this.isSyncingSubscriptions = false
        }
    }

    async migrateReviewRequested() {
        if (this.isMigratingReviewRequested) return

        const confirmResult = await Swal.fire({
            title: 'Run Migration?',
            html: `
                <p>This will mark all subscriptions older than 10 days as "review requested".</p>
                <p>This prevents review requests from being sent to old clients.</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, run migration',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.isMigratingReviewRequested = true

        try {
            const result = await this.backendService.migrateReviewRequested()

            await Swal.fire({
                title: 'Migration Complete!',
                html: `
                    <ul class="text-start">
                        <li><strong>${result.details?.migrated || 0}</strong> subscriptions marked as review requested</li>
                        <li><strong>${result.details?.skipped || 0}</strong> already marked (skipped)</li>
                    </ul>
                `,
                icon: 'success',
                confirmButtonText: 'OK',
            })
        } catch (error) {
            console.error('Error running migration:', error)
            await Swal.fire({
                title: 'Error',
                text: 'Failed to run migration. Please try again or check the console for details.',
                icon: 'error',
                confirmButtonText: 'OK',
            })
        } finally {
            this.isMigratingReviewRequested = false
        }
    }

    async sendReviewRequests() {
        if (this.isSendingReviewRequests) return

        const confirmResult = await Swal.fire({
            title: 'Send Review Requests?',
            html: `
                <p>This will send WhatsApp review requests to clients with active subscriptions > 4 hours old.</p>
                <p class="text-warning"><strong>Note:</strong> WhatsApp messages will be sent to real clients!</p>
                <p class="text-muted small">Make sure you've run the migration first to avoid messaging old clients.</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, send requests',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.isSendingReviewRequests = true

        try {
            const result = await this.backendService.sendReviewRequests()

            await Swal.fire({
                title: 'Success!',
                html: `
                    <ul class="text-start">
                        <li><strong>${result.details?.sent || 0}</strong> messages sent</li>
                        <li><strong>${result.details?.skippedAlreadyRequested || 0}</strong> already requested</li>
                        <li><strong>${result.details?.skippedNoPhone || 0}</strong> no phone number</li>
                        <li><strong>${result.details?.errors || 0}</strong> errors</li>
                    </ul>
                `,
                icon: 'success',
                confirmButtonText: 'OK',
            })
        } catch (error) {
            console.error('Error sending review requests:', error)
            await Swal.fire({
                title: 'Error',
                text: 'Failed to send review requests. Please try again or check the console for details.',
                icon: 'error',
                confirmButtonText: 'OK',
            })
        } finally {
            this.isSendingReviewRequests = false
        }
    }

    async resetIncorrectlyConvertedLeads() {
        if (this.isResettingIncorrectConverted) return

        const confirmResult = await Swal.fire({
            title: 'Reset Incorrectly Converted Leads?',
            html: `
                <p>This will find all CRM leads marked as "Converted" but who have <strong>never</strong> had an active or completed subscription.</p>
                <p>These leads will be reset to "New" status.</p>
                <p class="text-muted small">This is a one-time data cleanup migration.</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, reset leads',
            cancelButtonText: 'Cancel',
        })

        if (!confirmResult.isConfirmed) return

        this.isResettingIncorrectConverted = true

        try {
            const result = await this.backendService.resetIncorrectlyConvertedLeads()

            await Swal.fire({
                title: 'Migration Complete!',
                html: `
                    <ul class="text-start">
                        <li><strong>${result.totalConverted || 0}</strong> total leads marked as Converted</li>
                        <li><strong>${result.resetToNew || 0}</strong> reset to New (never had subscription)</li>
                        <li><strong>${result.correctlyConverted || 0}</strong> correctly marked as Converted</li>
                    </ul>
                `,
                icon: 'success',
                confirmButtonText: 'OK',
            })
        } catch (error) {
            console.error('Error resetting incorrectly converted leads:', error)
            await Swal.fire({
                title: 'Error',
                text: 'Failed to reset leads. Please try again or check the console for details.',
                icon: 'error',
                confirmButtonText: 'OK',
            })
        } finally {
            this.isResettingIncorrectConverted = false
        }
    }
}
